/**
 * UCP (agent-platform checkout) helpers — detection + presentation.
 *
 * Factored out of `MandateDetailV3.tsx` (2026-10-21) so the list row
 * (`VisitRecordRow`) and the per-event trust row inside the Activity
 * trace can share the same tiny decision ("is this a UCP record?",
 * "which platform is it from?") without importing the detail module.
 *
 * None of these helpers touch presentation state — they only read fields
 * that already live on the signed `AuditRecord` or its server-projected
 * `order_breakdown` /  `settlement` evidence.
 */

import type { AuditRecord } from '@sill/types';

/**
 * Shape of the server-projected `order_breakdown` the UCP relay attaches
 * to a UCP checkout record. We read fields defensively; the detail
 * module keeps the full typed `readOrderBreakdown()` reader.
 */
interface OrderBreakdownLike {
  readonly platform_host?: string;
  readonly platform_auth?: string | null;
  readonly line_items?: readonly unknown[];
}

/** Read `record.order_breakdown` without pulling in the full MandateDetail reader. */
function readBreakdown(record: AuditRecord): OrderBreakdownLike | undefined {
  const ob = (record as unknown as { order_breakdown?: OrderBreakdownLike }).order_breakdown;
  return typeof ob === 'object' && ob !== null ? ob : undefined;
}

/**
 * `true` when the record is a UCP (agent checkout) order. We check both
 * sides so an older record that pre-dates the signed `inbound_protocol`
 * but carries a UCP checkout breakdown still reads as UCP, and vice-versa.
 */
export function isUcpOrder(record: AuditRecord): boolean {
  if (record.inbound_protocol === 'ucp') return true;
  return readBreakdown(record) !== undefined;
}

/**
 * How the agent platform proved its identity on this order (ADR-0215 §11):
 * `signature` — every change was RFC 9421-signed and verified;
 * `api_key` — at least one change carried only the access key the
 * merchant issued for that platform. Read from the signed
 * `intent_summary.platform_auth` first, then the order breakdown; `null`
 * when neither says, so callers must not claim either.
 */
export type UcpPlatformAuth = 'signature' | 'api_key';

export function ucpPlatformAuth(record: AuditRecord): UcpPlatformAuth | null {
  // The signed record's own value wins (it is in the audit chain); the
  // session-derived breakdown is the fallback for records that pre-date it.
  const signed = record.intent_summary?.platform_auth;
  if (signed === 'signature' || signed === 'api_key') return signed;
  const a = readBreakdown(record)?.platform_auth;
  return a === 'signature' || a === 'api_key' ? a : null;
}

/**
 * Agent platform host for a UCP record. Prefers the server-projected
 * `order_breakdown.platform_host`, falls back to parsing the agent_id
 * (`agent_ucp_<host_with_underscores>` → "ops.sill.so"). `null` when
 * neither is present — the caller then uses `'Agent platform'` as a
 * last-resort label and never invents a host.
 */
export function ucpPlatformHost(record: AuditRecord): string | null {
  const ob = readBreakdown(record);
  const fromBreakdown = ob?.platform_host;
  if (typeof fromBreakdown === 'string' && fromBreakdown.length > 0) return fromBreakdown;
  const agent_id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  if (typeof agent_id === 'string' && agent_id.startsWith('agent_ucp_')) {
    // `agent_ucp_ops_sill_so` → `ops.sill.so`. The on-wire slug uses
    // underscores for every `.`, so a straight reverse is safe (no legit
    // host contains an underscore). Hosts are lowercase.
    return agent_id.slice('agent_ucp_'.length).replace(/_/g, '.').toLowerCase();
  }
  return null;
}

/**
 * `true` when a UCP record is Sill's own test platform — either the
 * staff-run test flag is set, or the resolved platform host is one of
 * Sill's known ops stations. These rows carry the Sill mark in the
 * detail header and the Activity list, exactly like a Sill-owned brand
 * logo would. The ✓ Confirmed shield is still drawn separately from the
 * server verdict; the mark is a claim, not proof.
 */
export function isUcpSillBranded(record: AuditRecord): boolean {
  if (record.staff_test === true) return true;
  const host = ucpPlatformHost(record);
  if (host === null) return false;
  // ops.sill.so is Sill's internal ops console; staff-test records come
  // from there and the host string matches. A future second ops host
  // (ops-eu.sill.so, say) would be added here, not inferred by substring
  // match (that would let a `fake.ops.sill.so.example` slip in).
  return host === 'ops.sill.so';
}
